/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type { PaginationQueryRequest } from '@vsolv/dev-kit/ngx';
import { Warranty, WarrantyDocument } from '@wsphere/warranties/domain';
import { catchError, firstValueFrom, of } from 'rxjs';

const api = `/api/warranties`;
@Injectable()
export class WarrantyCustomerPortalService {
  constructor(private http: HttpClient) {}

  getOneByCustomerEmail(email: string) {
    return firstValueFrom(
      this.http.get<Warranty.Model>(`${api}/email`, { params: { email } }).pipe(
        catchError(err => {
          if (err.status === 404) return of(null);
          throw err;
        })
      )
    );
  }

  getOneByContractNumber(contractNumber: string) {
    return firstValueFrom(
      this.http
        .get<Warranty.Model>(`${api}/${contractNumber}`, {
          params: {
            idField: 'contractNumber',
          },
        })
        .pipe(
          catchError(err => {
            if (err.status === 404) return of(null);
            throw err;
          })
        )
    );
  }

  list(params: Pick<Warranty.ListRequest, 'page' | 'limit'>): Promise<Warranty.ListResponse> {
    return firstValueFrom(
      this.http.get<Warranty.ListResponse>(`${api}/list`, {
        params: {
          ...(params.page ? { page: params.page.toString() } : {}),
          ...(params.limit ? { limit: params.limit.toString() } : {}),
        },
      })
    );
  }

  uploadWarrantyDocument(
    warrantyId: string,
    file: File,
    metadata: WarrantyDocument.UploadMetadata
  ): Promise<WarrantyDocument.UploadResponse> {
    const formData = new FormData();
    formData.set('file', file);
    formData.set('title', metadata.title);
    formData.set('category', metadata.category);
    if (metadata.serviceDate?.trim()) formData.set('serviceDate', metadata.serviceDate.trim());
    if (metadata.assetId?.trim()) formData.set('assetId', metadata.assetId.trim());

    return firstValueFrom(this.http.post<WarrantyDocument.UploadResponse>(`${api}/${warrantyId}/documents`, formData));
  }

  listWarrantyDocuments(
    warrantyId: string,
    params: PaginationQueryRequest = {}
  ): Promise<WarrantyDocument.ListResponse> {
    return firstValueFrom(
      this.http.get<WarrantyDocument.ListResponse>(`${api}/${warrantyId}/documents`, {
        params: {
          ...(params.page !== undefined ? { page: params.page.toString() } : {}),
          ...(params.limit !== undefined ? { limit: params.limit.toString() } : {}),
        },
      })
    );
  }

  retrieveWarrantyDocument(warrantyId: string, documentId: string): Promise<WarrantyDocument.DocumentResponse> {
    return firstValueFrom(
      this.http.get<WarrantyDocument.DocumentResponse>(`${api}/${warrantyId}/documents/${documentId}`)
    );
  }

  getWarrantyDocumentDownloadUrl(warrantyId: string, documentId: string): Promise<WarrantyDocument.DownloadResponse> {
    return firstValueFrom(
      this.http.get<WarrantyDocument.DownloadResponse>(`${api}/${warrantyId}/documents/${documentId}/download`)
    );
  }

  removeWarrantyDocument(warrantyId: string, documentId: string): Promise<void> {
    return firstValueFrom(this.http.delete<void>(`${api}/${warrantyId}/documents/${documentId}`));
  }

  checkRenewalEligibility(warrantyId: string) {
    return firstValueFrom(this.http.get<Warranty.RenewalEligibility>(`${api}/${warrantyId}/renewal/eligibility`));
  }

  previewRenewal(warrantyId: string) {
    return firstValueFrom(this.http.post<Warranty.RenewalPreview>(`${api}/${warrantyId}/renewal/preview`, {}));
  }

  confirmRenewal(warrantyId: string, dto: Warranty.ConfirmRenewalRequest) {
    return firstValueFrom(this.http.post<Warranty.RenewalResult>(`${api}/${warrantyId}/renewal/confirm`, dto));
  }

  async activateWarranty(warrantyId: string, body: Warranty.Activate) {
    return await firstValueFrom(this.http.post<Warranty.Activate>(`${api}/${warrantyId}/activate`, body)).catch(
      () => undefined
    );
  }
}
